import * as React from "react";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import { cn } from "@/lib/cn";

type Tone = "error" | "warning" | "success" | "info";

const toneStyle: Record<Tone, { box: string; Icon: typeof Info }> = {
  error: { box: "border-destructive/30 bg-destructive/[0.06] text-destructive-ink", Icon: XCircle },
  warning: { box: "border-warning/40 bg-warning/[0.08] text-warning-ink", Icon: AlertTriangle },
  success: { box: "border-success/30 bg-success/[0.08] text-success-ink", Icon: CheckCircle2 },
  info: { box: "border-info/30 bg-info/[0.06] text-info-ink", Icon: Info },
};

/**
 * A message about the thing the operator just did, beside the thing they did.
 *
 * `role="status"` rather than `role="alert"`: alert interrupts whatever a
 * screen reader is saying, which is right for "the site is down" and wrong for
 * "saved". The error tone uses `alert`, because that one IS the interruption.
 */
export function Notice({
  tone = "info",
  title,
  children,
  className,
}: {
  tone?: Tone;
  title?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const { box, Icon } = toneStyle[tone];
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn("flex gap-2.5 rounded border px-3 py-2.5 text-sm", box, className)}
    >
      <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 space-y-1">
        {title && <div className="font-medium">{title}</div>}
        {children}
      </div>
    </div>
  );
}

/**
 * The warnings a successful write came back with.
 *
 * They ride with `ok: true` on purpose -- the row saved, and what they describe
 * is a judgement the build will later refuse (a duplicate title) or a
 * readability problem. Shown as a WARNING rather than an error, because an
 * operator who is told "failed" about something that saved will try again.
 */
export function Warnings({ warnings }: { warnings?: string[] }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <Notice tone="warning" title={warnings.length === 1 ? "Saved, with one note" : `Saved, with ${warnings.length} notes`}>
      <ul className="list-disc space-y-0.5 pl-4">
        {warnings.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </ul>
    </Notice>
  );
}
